/**
 * Text normalisation and substitution reading of a cryptogram, mirrored from the server's
 * `cipher.py` (ADR 0060); the shared fixtures keep both in step. Plus what only the client
 * does: the live clash check and the editor's key generator.
 *
 * Only the 26 letters A-Z are enciphered. Everything else is kept as it is.
 */
import { ALPHABET, type Mapping } from "./types";

/** Letters NFKD leaves whole: spelled out, the way a cryptogram would print them. */
const LIGATURES: Record<string, string> = {
  Œ: "OE",
  œ: "OE",
  Æ: "AE",
  æ: "AE",
  ß: "SS",
  ẞ: "SS",
};

export function isCipherLetter(char: string): boolean {
  return char.length === 1 && char >= "A" && char <= "Z";
}

/** Mirrors `normalize_text`: uppercase, diacritics removed, ligatures spelled out. */
export function normalizeText(text: string): string {
  const spelled = Array.from(text, (c) => LIGATURES[c] ?? c).join("");
  return spelled.normalize("NFKD").replace(/\p{M}/gu, "").normalize("NFC").toUpperCase();
}

/** Mirrors `normalize_letter`: one typed character as a plain letter, or "". */
export function normalizeLetter(char: string): string {
  const letter = normalizeText(char);
  return isCipherLetter(letter) ? letter : "";
}

/** Mirrors `foreign_letters`: positions of letters outside A-Z. */
export function foreignLetters(text: string): number[] {
  const out: number[] = [];
  Array.from(text).forEach((c, i) => {
    if (/\p{L}/u.test(c) && !isCipherLetter(c)) out.push(i);
  });
  return out;
}

/** Mirrors `cipher_letters`: the distinct cipher letters, in order of first appearance. */
export function cipherLetters(ciphertext: string): string[] {
  const seen = new Set<string>();
  for (const char of ciphertext) if (isCipherLetter(char)) seen.add(char);
  return [...seen];
}

export type ShapeMismatch = "wrong_length" | "punctuation_differs";

/** Mirrors `shape_mismatch`. */
export function shapeMismatch(ciphertext: string, plaintext: string): ShapeMismatch | null {
  if (ciphertext.length !== plaintext.length) return "wrong_length";
  for (let i = 0; i < ciphertext.length; i++) {
    const c = ciphertext[i]!;
    const p = plaintext[i]!;
    if (isCipherLetter(c) !== isCipherLetter(p) || (!isCipherLetter(c) && c !== p))
      return "punctuation_differs";
  }
  return null;
}

export interface Reading {
  key: Mapping;
  /** Cipher letters read as two different plain letters. */
  ambiguous: string[];
  /** Plain letters standing for two different cipher letters. */
  reused: string[];
}

/** Mirrors `read_key`. Assumes the shapes match. */
export function readKey(ciphertext: string, plaintext: string): Reading {
  const reading: Reading = { key: {}, ambiguous: [], reused: [] };
  const owner: Mapping = {};
  for (let i = 0; i < ciphertext.length; i++) {
    const c = ciphertext[i]!;
    const p = plaintext[i]!;
    if (!isCipherLetter(c)) continue;
    const known = (reading.key[c] ??= p);
    if (known !== p) {
      if (!reading.ambiguous.includes(c)) reading.ambiguous.push(c);
      continue;
    }
    const first = (owner[p] ??= c);
    if (first !== c && !reading.reused.includes(p)) reading.reused.push(p);
  }
  return reading;
}

/** Mirrors `apply_key`: the text a key reads, or null while a letter is unmapped. */
export function applyKey(ciphertext: string, mapping: Mapping): string | null {
  let out = "";
  for (const char of ciphertext) {
    if (!isCipherLetter(char)) {
      out += char;
      continue;
    }
    const plain = mapping[char];
    if (!plain) return null;
    out += plain;
  }
  return out;
}

/**
 * Live feedback: the cipher letters whose plain letter is also written under another cipher
 * letter. A substitution is one-to-one, so at least one of them is wrong — which one, only the
 * key knows, so every one the player wrote is flagged. A given is public and locked: it is
 * never flagged, the letter clashing with it is. Nothing else is said (sound, CLAUDE.md §9).
 * Alphabetical, like the shared fixtures.
 */
export function clashes(mapping: Mapping, givens: Mapping, letters: string[]): string[] {
  const owners = new Map<string, string[]>();
  for (const cipher of letters) {
    const plain = givens[cipher] ?? mapping[cipher];
    if (!plain) continue;
    const list = owners.get(plain) ?? [];
    list.push(cipher);
    owners.set(plain, list);
  }
  const flagged = new Set<string>();
  for (const list of owners.values()) {
    if (list.length < 2) continue;
    for (const cipher of list) if (!(cipher in givens)) flagged.add(cipher);
  }
  return [...flagged].sort();
}

/** A small seeded generator (mulberry32): the same seed always draws the same key. */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A random enciphering key, plain letter → cipher letter, in which no letter stands for
 * itself (the classic rule the default rules promise). Shuffled, then each fixed point swapped
 * with its neighbour — a derangement for any seed.
 */
export function randomKey(seed: number): Mapping {
  const random = seededRandom(seed);
  const images = [...ALPHABET];
  for (let i = images.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [images[i], images[j]] = [images[j]!, images[i]!];
  }
  for (let i = 0; i < images.length; i++) {
    if (images[i] === ALPHABET[i]) {
      const j = (i + 1) % images.length;
      [images[i], images[j]] = [images[j]!, images[i]!];
    }
  }
  return Object.fromEntries(ALPHABET.map((letter, i) => [letter, images[i]!]));
}

/** The plaintext enciphered with a key (plain → cipher), after normalisation. */
export function encipher(plaintext: string, key: Mapping): string {
  return Array.from(normalizeText(plaintext), (c) => (isCipherLetter(c) ? (key[c] ?? c) : c)).join(
    "",
  );
}
